import UtilService from "./UtilService";

const AuditlogMetadataService = {
  /**
   * Extracts "old value" from metadata based on event type
   */
  extractOldValue: function (metadata, eventType) {
    if (!metadata) return "N/A";

    // ACTION-specific logic
    if (metadata.action === "ADDED") return "-";
    if (metadata.action === "REMOVED") {
      return this.extractRemovedValue(metadata);
    }

    // Find fields with old/new structure
    const fieldsWithOldNew = this.findFieldsWithOldNew(metadata);
    if (fieldsWithOldNew.length > 0) {
      const formatted = fieldsWithOldNew
        .slice(0, 2) // First 2 fields
        .map((f) => {
          const formattedValue = this.formatValue(f.old);
          // If value starts with "#", it's already formatted (collection with indices)
          if (typeof formattedValue === "string" && formattedValue.startsWith("#")) {
            return formattedValue;
          }
          return `${UtilService.camelCaseToReadable(f.name)}: ${formattedValue}`;
        })
        .join(", ");

      if (fieldsWithOldNew.length > 2) {
        return `${formatted} ... (${fieldsWithOldNew.length - 2} more)`;
      }
      return formatted;
    }

    // Check for "changes" map
    if (metadata.changes) {
      return this.extractFromChangesMap(metadata.changes, "old");
    }

    return "N/A";
  },

  /**
   * Extracts "new value" from metadata based on event type
   */
  extractNewValue: function (metadata, eventType) {
    if (!metadata) return "N/A";

    // ACTION-specific logic
    if (metadata.action === "REMOVED") return "-";
    if (metadata.action === "ADDED") {
      return this.extractAddedValue(metadata);
    }

    // Find fields with old/new structure
    const fieldsWithOldNew = this.findFieldsWithOldNew(metadata);
    if (fieldsWithOldNew.length > 0) {
      const formatted = fieldsWithOldNew
        .slice(0, 2) // First 2 fields
        .map((f) => {
          const formattedValue = this.formatValue(f.new);
          // If value starts with "#", it's already formatted (collection with indices)
          if (typeof formattedValue === "string" && formattedValue.startsWith("#")) {
            return formattedValue;
          }
          return `${UtilService.camelCaseToReadable(f.name)}: ${formattedValue}`;
        })
        .join(", ");

      if (fieldsWithOldNew.length > 2) {
        return `${formatted} ... (${fieldsWithOldNew.length - 2} more)`;
      }
      return formatted;
    }

    // Check for "changes" map
    if (metadata.changes) {
      return this.extractFromChangesMap(metadata.changes, "new");
    }

    return "N/A";
  },

  /**
   * Extract value for REMOVED action
   */
  extractRemovedValue: function (metadata) {
    // Try to find the removed entity
    if (metadata.pan) return metadata.pan;
    if (metadata.panHash) return metadata.panHash;
    if (metadata.cardDeckId) return `CardDeck: ${metadata.cardDeckId}`;
    if (metadata.merchantId) return `Merchant: ${metadata.merchantId}`;
    if (metadata.ruleType) return metadata.ruleType;
    if (metadata.targetType) return metadata.targetType;

    return "Removed";
  },

  /**
   * Extract value for ADDED action
   */
  extractAddedValue: function (metadata) {
    // Try to find the added entity
    if (metadata.pan) return metadata.pan;
    if (metadata.panHash) return metadata.panHash;
    if (metadata.cardDeckId) return `CardDeck: ${metadata.cardDeckId}`;
    if (metadata.merchantId) return `Merchant: ${metadata.merchantId}`;
    if (metadata.ruleType) return metadata.ruleType;
    if (metadata.targetType) return metadata.targetType;

    return "Added";
  },

  /**
   * Finds all fields with { old: X, new: Y } structure
   */
  findFieldsWithOldNew: function (metadata) {
    const result = [];
    for (const [key, value] of Object.entries(metadata)) {
      if (
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        ("old" in value || "new" in value)
      ) {
        result.push({ name: key, old: value.old, new: value.new });
      }
    }
    return result;
  },

  /**
   * Extract from changes map (for legacy format)
   */
  extractFromChangesMap: function (changes, field) {
    const entries = Object.entries(changes)
      .slice(0, 2)
      .map(([key, value]) => {
        const val =
          field === "old" ? value.old || value.from : value.new || value.to;
        return `${UtilService.camelCaseToReadable(key)}: ${this.formatValue(val)}`;
      });

    if (Object.keys(changes).length > 2) {
      return `${entries.join(", ")} ... (${Object.keys(changes).length - 2} more)`;
    }
    return entries.join(", ");
  },

  /**
   * Formats a value for display
   */
  formatValue: function (value) {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "YES" : "NO";
    if (Array.isArray(value)) {
      if (value.length === 0) return "[]";
      if (value.length <= 3) return `[${value.join(", ")}]`;
      return `[${value.slice(0, 3).join(", ")} ... +${value.length - 3}]`;
    }
    if (typeof value === "object") {
      const keys = Object.keys(value);
      if (keys.length === 0) return "{}";
      if (keys.length <= 2) return JSON.stringify(value);
      return `{${keys.slice(0, 2).join(", ")} ... +${keys.length - 2}}`;
    }

    // Check if ISO datetime
    if (typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
      return UtilService.isoDateToReadable(value);
    }

    // Truncate long strings
    const str = String(value);
    if (str.length > 50) {
      return str.substring(0, 47) + "...";
    }

    return str;
  },

  /**
   * Formats entire metadata for display in expanded row
   */
  formatMetadataForDisplay: function (metadata) {
    if (!metadata || Object.keys(metadata).length === 0) {
      return "No metadata available";
    }
    return JSON.stringify(metadata, null, 2);
  },

  /**
   * Returns categorized list of all event types
   */
  getEventTypeGroups: function () {
    return {
      "Identity & Access": [
        "USER_LOGIN",
        "USER_LOGIN_FAILED",
        "USER_LOGOUT",
      ],
      Killswitches: [
        "CARD_LOCK_KILLSWITCH_ACTIVATED",
        "CARD_LOCK_KILLSWITCH_DEACTIVATED",
        "CARD_LOCK_KILLSWITCH_MODIFIED",
        "KILLSWITCH_ACTIVATED",
        "KILLSWITCH_DEACTIVATED",
        "KILLSWITCH_MODIFIED",
        "KILLSWITCH_REDUNDANT_ACTIVATED",
        "KILLSWITCH_REDUNDANT_DEACTIVATED",
      ],
      "PAN Management": [
        "PAN_SUPPRESS_ADDED",
        "PAN_SUPPRESS_REMOVED",
        "PAN_SUPPRESS_MODIFIED",
        "VULNERABLE_PAN_ADDED",
        "VULNERABLE_PAN_REMOVED",
      ],
      Rules: [
        "AUTO_DECLINE_RULE_ADDED",
        "AUTO_DECLINE_RULE_REMOVED",
        "AUTO_DECLINE_RULE_MODIFIED",
        "RULE_SUPPRESS_ACTIVATED",
        "RULE_SUPPRESS_DEACTIVATED",
      ],
      Hotspots: [
        "HOTSPOT_ADDED_MANUAL",
        "HOTSPOT_ADDED_AUTO",
        "HOTSPOT_REMOVED_MANUAL",
        "HOTSPOT_REMOVED_AUTO",
        "HOTSPOT_STATUS_CHANGED",
      ],
      "Auto-Update Configs": [
        "CARD_AGE_AUTO_UPDATE_CHANGED",
        "COMPROMISED_PAN_AUTO_UPDATE_CHANGED",
        "COUNTRY_AUTO_UPDATE_CHANGED",
        "CUSTOMER_ORIGIN_AUTO_UPDATE_CHANGED",
        "DAYTIME_AUTO_UPDATE_CHANGED",
        "FACILITY_AUTO_UPDATE_CHANGED",
        "HOTSPOT_AUTO_UPDATE_CHANGED",
        "ODOMETER_AUTO_UPDATE_CHANGED",
        "VOLUME_AUTO_UPDATE_CHANGED",
        "AUTO_DECLINE_AUTO_UPDATE_CHANGED",
      ],
      "Threshold & Weight Configs": [
        "POS_THRESHOLD_CHANGED",
        "POS_WEIGHTS_CHANGED",
        "EW_PAY_THRESHOLD_CHANGED",
        "EW_PAY_WEIGHTS_CHANGED",
        "THRESHOLD_CONFIG_CHANGED",
        "THRESHOLD_MANUAL_CONFIG_CHANGED",
        "WEIGHT_CONFIG_CHANGED",
        "WEIGHT_MANUAL_CONFIG_CHANGED",
        "EW_PAY_THRESHOLD_CONFIG_CHANGED",
        "EW_PAY_THRESHOLD_MANUAL_CONFIG_CHANGED",
        "EW_PAY_WEIGHT_CONFIG_CHANGED",
        "EW_PAY_WEIGHT_MANUAL_CONFIG_CHANGED",
      ],
      "POS Rules": [
        "AUTO_ALLOW_RULE_CHANGED",
        "CARD_AGE_RULE_CHANGED",
        "CARD_LOCK_AUTO_ALLOW_RULE_CHANGED",
        "COMPROMISED_PAN_RULE_CHANGED",
        "COUNTRY_RULE_CHANGED",
        "CUSTOMER_COUNTRY_HISTORY_RULE_CHANGED",
        "CUSTOMER_HISTORY_RULE_CHANGED",
        "CUSTOMER_ORIGIN_RULE_CHANGED",
        "DAYTIME_RULE_CHANGED",
        "FACILITY_RULE_CHANGED",
        "FUEL_GUARD_AUTO_ALLOW_RULE_CHANGED",
        "HOTSPOT_RULE_CHANGED",
        "HOTSPOT_QUOTIENT_RULE_CHANGED",
        "IMMEDIATE_HOTSPOT_RULE_CHANGED",
        "RECENT_HOTSPOT_RULE_CHANGED",
        "ODOMETER_RULE_CHANGED",
        "ODOMETER_HISTORY_RULE_CHANGED",
        "ODOMETER_INCREMENT_HISTORY_RULE_CHANGED",
        "ODOMETER_LENGTH_HISTORY_RULE_CHANGED",
        "ODOMETER_OUTWORN_INCREMENT_RULE_CHANGED",
        "REJECT_HISTORY_RULE_CHANGED",
        "SIMILAR_ODOMETER_RULE_CHANGED",
        "SUDDEN_HOTSPOT_REJECT_RULE_CHANGED",
        "SUDDEN_HOTSPOT_VISIT_RULE_CHANGED",
        "SUSPICIOUS_PASSAGE_RULE_CHANGED",
        "TRIAL_TRANSACTION_RULE_CHANGED",
        "VARYING_ODOMETER_RULE_CHANGED",
        "VELOCITY_RULE_CHANGED",
        "VOLUME_RULE_CHANGED",
        "VOLUME_HISTORY_RULE_CHANGED",
        "INSUFFICIENT_PIN_EFFORT_RULE_CHANGED",
        "CUSTOMER_HISTORY_PATTERN_RULE_CHANGED",
      ],
      "Auth Reject Rules": [
        "ANY_AUTH_REJECT_RULE_CHANGED",
        "CARD_DECK_AUTH_REJECT_RULE_CHANGED",
        "CARD_LOCK_AUTH_REJECT_RULE_CHANGED",
        "FRAUD_SUSPICION_AUTH_REJECT_RULE_CHANGED",
        "FUEL_GUARD_AUTH_REJECT_RULE_CHANGED",
        "PABLO_AUTH_REJECT_RULE_CHANGED",
      ],
      "EW Pay Rules": [
        "CUSTOMER_CARD_DECK_EW_PAY_RULE_CHANGED",
        "CUSTOMER_LIMIT_EW_PAY_RULE_CHANGED",
        "CUSTOMER_SUDDEN_HOTSPOT_VISIT_EW_PAY_RULE_CHANGED",
      ],
      "Other Configs": [
        "NOTIFICATION_CONFIG_CHANGED",
        "SIMULATION_CONFIG_CHANGED",
        "SUCCESS_STATISTICS_CONFIG_CHANGED",
        "CUSTOMER_HISTORY_PATTERN_UPDATE_CONFIG_CHANGED",
        "WEIGHT_UPDATE_CONFIG_CHANGED",
        "QUALITY_GATE_CONFIG_CHANGED",
      ],
      "Legacy (Deprecated)": [
        "CONFIG_CHANGED",
        "GENERAL_CONFIG_CHANGED",
        "MERCHANT_HOTSPOT_CHANGED",
        "AUTO_UPDATE_CONFIG_CHANGED",
        "WEIGHT_CONFIG_CHANGED_LEGACY",
        "SUPPRESS_CONFIG_CHANGED",
      ],
    };
  },
};

export default AuditlogMetadataService;
